/**
 * CL-LIST-01 — seller listing deletion rules, shared by the browser and the Express server.
 *
 * CommonJS with ZERO dependencies, for listingDiscountRules.js's reason: `server/` cannot import
 * ESM from `src/`, and the webpack build forbids a client import from outside `src/`. So the key
 * names and predicates live here once (brief SL1), and a second spelling anywhere is a bug.
 *
 * All three keys live in listing `metadata`: only an operator-level client (our server, through
 * the Integration API) can write it, so a seller cannot un-delete from the browser. It is PUBLIC
 * to read (brief I) — never add a reason or a `deletedBy` key here.
 */

const DELETION_KEYS = Object.freeze({
  deleted: 'sellerDeleted',
  deletedAt: 'sellerDeletedAt',
  fromState: 'sellerDeletedFromState',
});

// Registered in the listing search schema: scope metadata, type boolean, DEFAULT false (Task 1).
// The default is what makes a listing without the key match `false`, so nothing is back-filled.
const SELLER_DELETED_FILTER = Object.freeze({ [`meta_${DELETION_KEYS.deleted}`]: false });
const SELLER_DELETED_ONLY_FILTER = Object.freeze({ [`meta_${DELETION_KEYS.deleted}`]: true });

// The Marketplace/Integration API listing states, as strings (src/util/types.js has the same
// values as ESM constants, which server/ cannot import).
const LISTING_STATES = Object.freeze({
  draft: 'draft',
  pendingApproval: 'pendingApproval',
  published: 'published',
  closed: 'closed',
});

const isSoftDeleted = listing => listing?.attributes?.metadata?.[DELETION_KEYS.deleted] === true;

// D4 / SL8: a row that is missing, hard-deleted, not published or soft-deleted is not for sale.
const isForSale = listing =>
  !!listing?.attributes &&
  listing.attributes.deleted !== true &&
  listing.attributes.state === LISTING_STATES.published &&
  !isSoftDeleted(listing);

const nonEmptyString = v => typeof v === 'string' && v.length > 0;

/**
 * The only metadata this feature ever writes (AC19). An existing timestamp and from-state are
 * kept (SL4, AC21): the timestamp is the operator's only purge clock, and on a retry after a
 * half-finished delete the listing is already closed, so the CURRENT state would misreport it.
 */
const buildDeletionMetadata = ({ fromState, existing, nowIso }) => {
  const prev = existing || {};
  return {
    [DELETION_KEYS.deleted]: true,
    [DELETION_KEYS.deletedAt]: nonEmptyString(prev[DELETION_KEYS.deletedAt])
      ? prev[DELETION_KEYS.deletedAt]
      : nowIso,
    [DELETION_KEYS.fromState]: nonEmptyString(prev[DELETION_KEYS.fromState])
      ? prev[DELETION_KEYS.fromState]
      : fromState,
  };
};

// OPEN-7. A transaction is FINISHED only in a state nothing can leave: the terminal states of
// clastique-purchase, clastique-negotiation and default-inquiry (checked against their
// process.edn: no transition has one of these as :from). Listed as the FINISHED set on purpose —
// every other state, including `completed`, `reviewed-by-*` and any state a future process adds,
// counts as unfinished. A false "unfinished" delays a purge; a false "finished" breaks an order.
const FINISHED_TX_STATES = Object.freeze([
  'state/reviewed',
  'state/canceled',
  'state/payment-expired',
  'state/offer-declined',
  'state/offer-expired',
  'state/offer-rejected',
  'state/request-rejected',
  'state/free-inquiry',
]);

const isUnfinishedTxState = state => !FINISHED_TX_STATES.includes(state);

module.exports = {
  DELETION_KEYS,
  SELLER_DELETED_FILTER,
  SELLER_DELETED_ONLY_FILTER,
  LISTING_STATES,
  isSoftDeleted,
  isForSale,
  buildDeletionMetadata,
  FINISHED_TX_STATES,
  isUnfinishedTxState,
};
